/**
 * Parses a bedroom endpoint ("studio", "std", "0", "1", …) into a numeric
 * bed count. Studio / std / 0 → 0. Returns null when the value isn't usable.
 */
function parseBedEndpoint(raw: string): number | null {
  const lower = raw.trim().toLowerCase();
  if (!lower) return null;
  if (lower === 'studio' || lower === 'std' || lower === '0') return 0;
  const num = parseInt(lower, 10);
  return Number.isNaN(num) || num < 0 ? null : num;
}

/** Formats a bed count for display: 0 → "Studio", otherwise the number. */
function bedCountLabel(n: number): string {
  return n === 0 ? 'Studio' : String(n);
}

/**
 * Joins labels with commas and a final "&"
 * (e.g. ["1","2","3"] → "1, 2 & 3").
 */
function joinWithAnd(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} & ${items[items.length - 1]}`;
}

/**
 * Formats a raw bedrooms range (e.g. "studio - 2") into a listed set of
 * options like "Studio, 1 & 2 Bedroom". Handles single values
 * ("1" → "1 Bedroom"), expanded ranges ("1 - 3" → "1, 2 & 3 Bedroom"),
 * and studios. Ranges where both endpoints are the same are collapsed
 * (e.g. "2 - 2" → "2 Bedroom").
 */
export function formatBedroomsLabel(raw: string | null | undefined): string {
  if (!raw) return 'Apartments';
  const trimmed = raw.trim();
  if (!trimmed) return 'Apartments';

  // Strip a trailing "Bedroom(s)" so both raw field values and baked copy work.
  const withoutBedroom = trimmed.replace(/\s+bedrooms?$/i, '').trim();

  // Already a listed phrase (commas / "and" / "&") — don't re-format.
  if (/,/.test(withoutBedroom) || /\band\b|&/i.test(withoutBedroom)) {
    const allStudios = /^studio$/i.test(withoutBedroom);
    return allStudios ? withoutBedroom : /bedroom/i.test(trimmed) ? trimmed : `${withoutBedroom} Bedroom`;
  }

  // Normalize en/em dashes to ASCII hyphen before splitting.
  const normalized = withoutBedroom.replace(/[–—]/g, '-');
  const parts = normalized.split(/\s*-\s*/).map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return 'Apartments';

  const parsed = parts.map(parseBedEndpoint);
  const canExpand =
    parsed.length >= 1 &&
    parsed.every((n): n is number => n !== null);

  if (!canExpand) {
    const labels = parts.map((p) => {
      const lower = p.toLowerCase();
      if (lower === 'studio' || lower === 'std') return 'Studio';
      return p;
    });
    const deduped =
      labels.length > 1 && labels.every((p) => p === labels[0])
        ? [labels[0]]
        : labels;
    const allStudios = deduped.every((p) => p === 'Studio');
    const joined = joinWithAnd(deduped);
    return allStudios ? joined : `${joined} Bedroom`;
  }

  const min = Math.min(...parsed);
  const max = Math.max(...parsed);
  const counts: number[] = [];
  for (let n = min; n <= max; n++) counts.push(n);

  if (counts.length === 0) return 'Apartments';

  const labels = counts.map(bedCountLabel);
  const allStudios = counts.every((n) => n === 0);
  const joined = joinWithAnd(labels);
  return allStudios ? joined : `${joined} Bedroom`;
}

/**
 * Rewrites baked dash-style bedroom copy left over from older defaults
 * (e.g. "Studio - 2 Bedroom" → "Studio, 1 & 2 Bedroom").
 */
export function expandBakedBedroomRanges(html: string): string {
  // "Studio - 2 Bedroom" / "1 - 3 Bedrooms"
  let result = html.replace(
    /\b((?:Studio|Std|\d+)\s*[-–—]\s*(?:Studio|Std|\d+))\s+Bedrooms?\b/gi,
    (_match, range: string) => formatBedroomsLabel(range),
  );
  // Bare range before "Apartments" (field token resolved without a Bedroom suffix)
  result = result.replace(
    /\b((?:Studio|Std|\d+)\s*[-–—]\s*(?:Studio|Std|\d+))(?=\s+Apartments)/gi,
    (_match, range: string) => formatBedroomsLabel(range),
  );
  return result;
}

/**
 * Collapses repeated "Bedroom" words (optionally separated by whitespace,
 * &nbsp;, or inline tags) so a formatted range followed by authored
 * "Bedroom" copy reads "2 Bedroom Apartments", not "2 Bedroom Bedroom …".
 */
export function dedupeBedroomWord(html: string): string {
  const repeated = /\b(Bedrooms?)\b((?:\s|&nbsp;|<[^>]+>)+)Bedrooms?\b/gi;
  let result = html;
  let prev: string;
  do {
    prev = result;
    result = result.replace(repeated, '$1$2');
  } while (result !== prev);
  return result;
}
